import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useJsonLd, getSiteUrl } from "@/hooks/useJsonLd";

export default function ArticleWysylka() {
  const navigate = useNavigate();
  const siteUrl = getSiteUrl();
  useEffect(() => { window.scrollTo(0, 0); document.title = "Koszty Wysyłki Kakobuy do Polski – Zestawienie 2026 | Kakobuy Spreadsheet"; }, []);

  useJsonLd({
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Koszty Wysyłki Kakobuy do Polski – Pełne Zestawienie 2026",
    "description": "Ile kosztuje wysyłka z Kakobuy do Polski? Pełne zestawienie metod dostawy, cen i czasów w 2026 roku.",
    "url": `${siteUrl}/artykul/koszty-wysylki-kakobuy`,
    "datePublished": "2026-06-21",
    "dateModified": "2026-06-21",
    "inLanguage": "pl",
    "author": {
      "@type": "Organization",
      "name": "Kakobuy Spreadsheet Polska",
      "url": siteUrl,
    },
    "publisher": {
      "@type": "Organization",
      "name": "Kakobuy Spreadsheet Polska",
      "url": siteUrl,
    },
    "image": "https://readdy.ai/api/search-image?query=Shipping%20box%20with%20packing%20materials%20on%20light%20mint%20green%20surface%2C%20soft%20studio%20lighting%2C%20editorial%20product%20photography%2C%20delivery%20concept%2C%20minimal%20composition%2C%20clean%20fresh%20aesthetic&width=1600&height=900&seq=kako-art-06-hero&orientation=landscape",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteUrl}/artykul/koszty-wysylki-kakobuy`,
    },
  });

  return (
    <>
      <Navbar />
      <main>
        <section className="relative min-h-[55vh] flex flex-col overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url(https://readdy.ai/api/search-image?query=Shipping%20box%20with%20packing%20materials%20on%20light%20mint%20green%20surface%2C%20soft%20studio%20lighting%2C%20editorial%20product%20photography%2C%20delivery%20concept%2C%20minimal%20composition%2C%20clean%20fresh%20aesthetic&width=1600&height=900&seq=kako-art-06-hero&orientation=landscape)" }}></div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/35"></div>
          <div className="relative z-10 flex-1 flex items-center pt-24 pb-12">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <span className="inline-block px-4 py-1.5 bg-background-50/60 backdrop-blur-md rounded-full text-xs font-semibold text-foreground-800 mb-6 whitespace-nowrap">Blog &gt; Wysyłka</span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight">Koszty Wysyłki<br />Kakobuy do Polski</h1>
              <p className="text-base text-background-50/85 mt-4 max-w-2xl">Ile kosztuje wysyłka z Kakobuy do Polski? Pełne zestawienie metod dostawy, cen i czasów w 2026 roku.</p>
            </div>
          </div>
        </section>

        <article className="py-16 md:py-24 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="prose prose-lg max-w-none">
              <section className="mb-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">Metody wysyłki <span className="italic text-foreground-600 font-normal">Kakobuy Spreadsheet</span></h2>
                <div className="overflow-x-auto mb-6">
                  <table className="w-full border-collapse rounded-xl overflow-hidden">
                    <thead><tr className="bg-foreground-900"><th className="p-4 text-left text-sm font-semibold text-background-50">Metoda</th><th className="p-4 text-left text-sm font-semibold text-background-50">Czas dostawy</th><th className="p-4 text-left text-sm font-semibold text-background-50">Koszt</th></tr></thead>
                    <tbody>
                      <tr className="border-b border-background-200"><td className="p-4 text-sm font-medium text-foreground-800">Standard</td><td className="p-4 text-sm text-foreground-700">10-20 dni</td><td className="p-4 text-sm text-accent-700 font-semibold">od 40 zł</td></tr>
                      <tr className="border-b border-background-200 bg-background-50"><td className="p-4 text-sm font-medium text-foreground-800">Ekspresowa</td><td className="p-4 text-sm text-foreground-700">5-10 dni</td><td className="p-4 text-sm text-accent-700 font-semibold">od 80 zł</td></tr>
                      <tr><td className="p-4 text-sm font-medium text-foreground-800">Priorytetowa</td><td className="p-4 text-sm text-foreground-700">3-7 dni</td><td className="p-4 text-sm text-accent-700 font-semibold">od 120 zł</td></tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-foreground-700 leading-relaxed">Koszty wysyłki zależą od wagi paczki i wybranej metody. <strong>Kakobuy Spreadsheet</strong> umożliwia śledzenie przesyłki na każdym etapie — od magazynu w Chinach do twoich drzwi w Polsce.</p>
              </section>

              <section className="text-center py-10">
                <p className="text-lg text-foreground-700 mb-6">Sprawdź <strong>Kakobuy Spreadsheet</strong> i zamów z dostawą do Polski.</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="bg-foreground-900 text-background-50 px-7 py-3 rounded-full text-sm font-semibold hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer">Kakobuy Spreadsheet</a>
                  <a href="/" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer" onClick={(e) => { e.preventDefault(); navigate("/"); }}><i className="ri-arrow-left-line mr-1"></i>Strona główna</a>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}